import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  Injector,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { LocalStorageManager } from '@examdojo/browser/local-storage';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { TiptapTableOfContentsComponent } from '@examdojo/core/tiptap';
import { EditorWithPopoverService, Popover, PopoverService } from '@examdojo/core/tiptap-popover';
import { TiptapEditorWrapperComponent } from '@examdojo/core/tiptap-react-ui-components';
import { DashedSquareIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/dashed-square-icon';
import type { SlashMenuItem } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/slash-dropdown-menu/use-slash-dropdown-menu';
import { isNodeInSchema } from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-utils';
import { TextareaInputComponent } from '@examdojo/ui/input';
import type { Editor } from '@tiptap/react';
import { debounceTime, distinctUntilChanged, merge, tap } from 'rxjs';
import sampleContent from './data/sample-content.json';
import { KeyPointCalloutExtension } from './extensions/key-point-callout';

const STORAGE_KEY = 'study-notes-editor-content';

@Component({
  selector: 'dojo-study-notes-editor',
  templateUrl: './study-notes-editor.component.html',
  styleUrls: ['./study-notes-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full' },
  imports: [
    TiptapEditorWrapperComponent,
    ReactiveFormsModule,
    IconComponent,
    TextareaInputComponent,
    TiptapTableOfContentsComponent,
    ButtonComponent,
  ],
  providers: [PopoverService],
})
export class StudyNotesEditorComponent {
  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly injector: Injector,
    private readonly popoverService: PopoverService,
  ) {
    merge(this.syncTextarea(), this.syncEditor()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.configurePopoverService();
  }

  readonly editor = signal<Editor | null>(null);
  private readonly storageManager = new LocalStorageManager<{ content: string }>(STORAGE_KEY);

  readonly editorWrapper = viewChild<TiptapEditorWrapperComponent>('editorWrapper');
  private readonly scrollableContainerRef = viewChild<ElementRef<HTMLElement>>('scrollableContainerRef');

  readonly tableOfContentData = computed(() => this.editorWrapper()?.tableOfContentData() ?? []);
  readonly scrollableContainer = computed(() => this.scrollableContainerRef()?.nativeElement);

  readonly editorControl = new FormControl<string>(this.storageManager.getValue()?.content ?? '', {
    nonNullable: true,
  });

  readonly textareaControl = new FormControl<string>(this.storageManager.getValue()?.content ?? '', {
    nonNullable: true,
  });

  readonly editorOptions = computed(() => ({
    extensions: [KeyPointCalloutExtension(this.injector), Popover],
    onCreate: ({ editor }: { editor: EditorWithPopoverService }) => {
      this.editor.set(editor);
      editor.storage.popoverService = this.popoverService;
    },
  }));

  readonly slashMenuItems = computed<Record<string, SlashMenuItem>>(() => ({
    keypointCallout: {
      metadata: {
        title: 'Key Point',
        subtext: 'Highlighted key point box',
        keywords: ['keypoint', 'key point', 'callout'],
        badge: DashedSquareIcon,
        group: 'Study notes',
      },
      implementation: {
        check: (editor: Editor) => isNodeInSchema('keyPointCallout', editor),
        action: ({ editor }: { editor: Editor }) => {
          editor
            .chain()
            .focus()
            .insertContent({
              type: 'keyPointCallout',
              attrs: {
                title: 'Key Point',
                color: 'yellow',
                icon: 'key',
              },
              content: [
                {
                  type: 'paragraph',
                },
              ],
            })
            .run();
        },
      },
    },
  }));

  loadSampleContent(): void {
    const editor = this.editor();
    if (!editor) {
      return;
    }

    editor.commands.setContent(sampleContent);
  }

  private configurePopoverService(): void {
    this.popoverService.config = {
      allowEditing: true,
      onContentChange: (popoverId, htmlContent) => {
        const editor = this.editor();
        editor?.commands.updatePopoverById(popoverId, { htmlContent });
      },
      onLinkUrlChange: (popoverId, linkUrl) => {
        const editor = this.editor();
        editor?.commands.updatePopoverById(popoverId, { linkUrl: linkUrl || undefined });
      },
    };
  }

  private syncTextarea() {
    return this.textareaControl.valueChanges.pipe(
      distinctUntilChanged(),
      tap((value) => {
        this.editorControl.setValue(value, { emitEvent: false });
      }),
    );
  }

  private syncEditor() {
    return this.editorControl.valueChanges.pipe(
      tap((value) => {
        this.textareaControl.setValue(value, { emitEvent: false });
      }),
      debounceTime(500),
      distinctUntilChanged(),
      tap((value) => {
        this.storageManager.setValue({ content: value });
      }),
    );
  }
}
